import type { Response } from './index';
import { echossAxios } from './index';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface SurveyOpenPayload {
  svy: string;
}

export interface SurveyRewardCoupon {
  coupon_name: string;
  coupon_qty: number;
}

export interface SurveyReward {
  type: 1 | 2;
  require_member: boolean;
  coupons: SurveyRewardCoupon[] | null;
  points: number | null;
}

export type SurveyCtaMain = 'register' | 'view_coupon' | 'view_points' | 'none';

export interface SurveyCta {
  main: SurveyCtaMain;
}

export interface SurveyOpenData {
  response_id: number;
  survey: {
    id: number;
    name: string;
    welcome_text: string | null;
    question_count: number;
    estimated_minutes: number;
  };
  brand: {
    id: number;
    name: string;
    logo_url: string | null;
  };
  rewards: SurveyReward[];
  questions: SurveyQuestion[];
}

export interface SurveyQuestion {
  id: number;
  sort_order: number;
  type: 1 | 2 | 3 | 4 | 5;
  content: string;
  is_required: boolean;
  jump_to: number | null;
  conditional_jump: boolean | null;
  is_yesno: boolean | null;
  district_mode: 1 | 2 | null;
  options: SurveyQuestionOption[];
}

export interface SurveyQuestionOption {
  id: number;
  sort_order: number;
  text: string;
  jump_to: number | null;
}

export interface SurveySubmitPayload {
  response_id: number;
  answers: SurveyAnswer[];
}

export interface SurveyAnswer {
  question_id: number;
  value_int?: number | null;
  value_text?: string | null;
  single_option?: number | null;
  multi_options?: number[] | null;
  value_city_code?: string | null;
  value_district_code?: string | null;
}

export interface SurveySubmitData {
  response_id: number;
  completed_at: string;
  thanks_text: string | null;
  rewards: SurveyReward[];
  reward_status: 1 | 3 | 4;
  is_member: boolean;
  is_line_friend: boolean;
  cta: SurveyCta;
}

export interface SurveyErrorData {
  redirect_to: 'error' | 'filled' | null;
  completed_at?: string | null;
  next_open_date?: string | null;
  rewards?: SurveyReward[];
  cta?: SurveyCta;
  errors?: Record<string, string[]>;
}

export const openSurvey = async (
  token: string,
  payload: SurveyOpenPayload,
): Promise<Response<SurveyOpenData>> => {
  try {
    const { data } = await echossAxios.request<ApiResponse<SurveyOpenData>>({
      url: '/api/member/surveys/open',
      method: 'POST',
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { data: data.data, message: data.message };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

export const submitSurvey = async (
  token: string,
  payload: SurveySubmitPayload,
): Promise<Response<SurveySubmitData>> => {
  try {
    const { data } = await echossAxios.request<ApiResponse<SurveySubmitData>>({
      url: '/api/member/surveys/submit',
      method: 'POST',
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { data: data.data, message: data.message };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};
